import type { ReactNode } from 'react'
import { HelpCircle } from 'lucide-react'

import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/** The help icon on its own — for labels that are not a `FieldLabel`. */
export function FieldTooltip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="size-3.5 shrink-0 cursor-help text-(--ad-text-faint)" />
      </TooltipTrigger>
      <TooltipContent>
        {text.split('\n').map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  )
}

export interface FieldLabelProps {
  children: ReactNode
  /** Explanatory help — hidden behind a hover icon so the form stays clean. */
  tooltip?: string
  /** A short constraint, e.g. "Min 5.00 NOK" — greyed out next to the label. */
  hint?: string
  htmlFor?: string
  className?: string
}

/** The label of a `.p-field`, with optional hover help and a greyed-out hint. */
export function FieldLabel({ children, tooltip, hint, htmlFor, className }: FieldLabelProps) {
  return (
    <label htmlFor={htmlFor} className={cn('flex items-center gap-1.5', className)}>
      {children}
      {tooltip && <FieldTooltip text={tooltip} />}
      {hint && <span className="text-xs font-normal text-(--ad-text-muted)">{hint}</span>}
    </label>
  )
}
